import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import { useCreateFormTemplate } from "@/hooks/use-form-templates";
import {
  TemplateMetaForm,
  type TemplateMetaValues,
} from "@/components/form-templates/template-meta-form";
import { useToast } from "@/hooks/use-toast";
import { PageHeader, Breadcrumbs, Card } from "@/components/ui";

export const Route = createFileRoute("/_authenticated/test-program/templates/new")({
  component: FormTemplateNewPage,
});

/**
 * Creates an org_custom template family (identity only — no versions). The
 * first draft document arrives in the builder, which the page hands off to
 * on success.
 */
function FormTemplateNewPage() {
  const { t } = useTranslation(["form-templates", "common", "nav"]);
  const toasts = useToast();
  const navigate = useNavigate();
  const createMutation = useCreateFormTemplate();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [slugConflict, setSlugConflict] = useState<string | null>(null);

  const handleSubmit = (values: TemplateMetaValues) => {
    setSubmitError(null);
    setSlugConflict(null);
    if (!values.target_kind) return;
    createMutation.mutate(
      {
        title: values.title,
        slug: values.slug,
        target_kind: values.target_kind,
        ...(values.device_category ? { device_category: values.device_category } : {}),
      },
      {
        onSuccess: (created) => {
          toasts.success("form-templates:new.success");
          void navigate({
            to: "/test-program/templates/$id/builder",
            params: { id: created.id },
          });
        },
        onError: (err) => {
          // ApiError.details is `unknown`; the cast names only the OPTIONAL
          // `error` key, so any other envelope falls to the generic branch.
          if (
            err instanceof ApiError &&
            (err.details as { error?: string } | undefined)?.error === "form_template_slug_conflict"
          ) {
            setSlugConflict(t("errors.slugConflict"));
          } else {
            setSubmitError(t("errors.createFailed"));
          }
        },
      },
    );
  };

  return (
    <div className="p-6">
      <Breadcrumbs
        items={[
          {
            label: t("testProgram.templates", { ns: "nav" }),
            to: "/test-program",
            search: { tab: "templates" },
          },
          { label: t("new.title") },
        ]}
      />
      <PageHeader title={t("new.title")} />

      <div className="mt-6">
        <Card>
          <TemplateMetaForm
            mode="create"
            isSubmitting={createMutation.isPending}
            submitError={submitError}
            slugConflictError={slugConflict}
            onSubmit={handleSubmit}
            onCancel={() => void navigate({ to: "/test-program", search: { tab: "templates" } })}
          />
        </Card>
      </div>
    </div>
  );
}
